import routesLimited, { hasProperty } from '@/constants/routesLimited.js'
import { cloneDeep } from 'lodash'
import { castRouteIds } from 'plugins/router/routes/castRouteIds'
import { hideCis } from 'plugins/router/routes/cis'
import {
	craftsmanTask,
	craftsmanTasks,
} from 'plugins/router/routes/craftsmanTasks'
import { hideMeterReadingLimit } from 'plugins/router/routes/global'
import { imagePreview } from 'plugins/router/routes/global/imagePreview'
import { notes } from 'plugins/router/routes/notes'
import { hideTasks } from 'plugins/router/routes/task'
import type { HideObject, RouteWithParams } from 'plugins/router/types'
import type { RouteRecordRaw } from 'vue-router'
import { aliases } from '../aliases'

const buildingImageUpload: RouteRecordRaw = {
	name: 'imageUpload',
	path: 'imageupload',
	component: () =>
		import('components/modules/Buildings/Building/BuildingHeaderUpload.vue'),
	props: castRouteIds,
	meta: {
		limited: routesLimited.can_post,
		title: 'Titelbild hinterlegen',
	},
}

const hideInvoicing: HideObject = {
	method: hasProperty,
	parameter: {
		store: 'setting',
		getter: 'getData',
		property: 'hide_invoicing',
		value: false,
	},
}
const hideAccounting: HideObject = {
	method: hasProperty,
	parameter: {
		store: 'setting',
		getter: 'getData',
		property: 'hide_accounting',
		value: false,
	},
}
const accounting: RouteRecordRaw = {
	component: () =>
		import('@/components/modules/Buildings/Building/Accounting/Accounting.vue'),
	children: [
		{
			name: 'buildingInvoices',
			path: 'invoices',
			component: () =>
				import(
					'@/components/modules/Buildings/Building/Accounting/Invoices.vue'
				),
			meta: {
				icon: 'fal fa-file-invoice',
				title: 'routeTitle.invoices',
				hidden: hideInvoicing,
				limited: [
					routesLimited.isAdminOrTeam,
					{
						method: hasProperty,
						parameter: {
							store: 'user',
							getter: 'permissions',
							property: 'role.building_invoices',
							value: true,
						},
					},
				],
				subs: [routesLimited.subModules.invoicing],
			},
			props: (route: RouteWithParams) => {
				return {
					buildingId: parseInt(route.params.id),
				}
			},
		},
		{
			name: 'buildingAccounts',
			path: 'accounts',
			component: () =>
				import(
					'@/components/modules/Buildings/Building/Accounting/Accounts.vue'
				),
			meta: {
				icon: 'fal fa-box-dollar',
				title: 'Konten',
				hidden: hideAccounting,
				limited: [
					routesLimited.isAdminOrTeam,
					{
						method: hasProperty,
						parameter: {
							store: 'user',
							getter: 'permissions',
							property: 'role.building_accounts',
							value: true,
						},
					},
				],
				subs: [routesLimited.subModules.accounting],
			},
			props: (route: RouteWithParams) => {
				return {
					buildingId: parseInt(route.params.id as string),
					accountId: parseInt(route.params.accountId as string),
				}
			},
		},
	],
	path: 'accounting',
	name: 'buildingAccounting',
	props: castRouteIds,
	meta: {
		auth: true,
		nav: true,
		icon: 'fal fa-wallet',
		hidden: [hideInvoicing, hideAccounting],
		limited: [
			routesLimited.isAdminOrTeam,
			{
				method: hasProperty,
				parameter: {
					store: 'user',
					getter: 'permissions',
					property: 'role.building_accounts',
					value: true,
				},
			},
			{
				method: hasProperty,
				parameter: {
					store: 'user',
					getter: 'permissions',
					property: 'role.building_invoices',
					value: true,
				},
			},
		],
		subs: [
			routesLimited.subModules.invoicing || routesLimited.subModules.accounting,
		],
		ordering: 8,

		title: 'entity.building.tabs.accounting',
	},
}

const buildingDocumentsList: RouteRecordRaw = {
	component: () =>
		import('@/components/modules/Buildings/Building/Documents/List.vue'),
	meta: {
		title: 'common.list',
		subs: [routesLimited.subModules.documents],
	},
	name: 'buildingDocumentList',
	path: '',
	props: castRouteIds,
}

const buildingDocumentsGroup: RouteRecordRaw = {
	component: () =>
		import('@/components/modules/Buildings/Building/Documents/Types.vue'),
	meta: {
		title: 'common.group',
		subs: [routesLimited.subModules.documents],
	},
	name: 'buildingDocumentTypes',
	path: 'types',
	props: castRouteIds,
}

const buildingMetersDetails: RouteRecordRaw = {
	path: ':meterId',
	component: () =>
		import('@/components/modules/Buildings/Building/Meters/Details.vue'),
	name: 'buildingMeterDetails',
	props: true,
}
const buildingMetersLevelInput: RouteRecordRaw = {
	path: ':meterId/level',
	component: () =>
		import('@/components/modules/Buildings/Building/Meters/LevelInput.vue'),
	name: 'meterLevel',
	props: true,
}

const unitPersons: RouteRecordRaw = {
	component: () =>
		import('components/modules/Buildings/Building/Units/UnitPersons.vue'),
	meta: {
		title: 'routeTitle.persons',
		subs: [routesLimited.subModules.persons],
	},
	name: 'unitPersons',
	path: 'persons',
	props: castRouteIds,
}
const unitDocuments: RouteRecordRaw = {
	component: () =>
		import('components/modules/Buildings/Building/Units/UnitDocuments.vue'),
	meta: {
		title: 'routeTitle.documents',
		subs: [routesLimited.subModules.documents],
	},
	name: 'unitDocuments',
	path: 'documents',
	props: castRouteIds,
}

const unitMetersDetails: RouteRecordRaw = {
	...buildingMetersDetails,
	...{ name: 'unitMeterDetails' },
}
const unitMeters: RouteRecordRaw = {
	component: () =>
		import('components/modules/Buildings/Building/Units/UnitMeters.vue'),
	meta: {
		title: 'routeTitle.meters',
		hidden: hideMeterReadingLimit,
		subs: [routesLimited.subModules.meters],
	},
	children: [unitMetersDetails],
	name: 'unitMeters',
	path: 'meters',
	props: castRouteIds,
}
const unit: RouteRecordRaw = {
	children: [unitPersons, unitDocuments, unitMeters],
	component: () =>
		import('@/components/modules/Buildings/Building/Units/Unit.vue'),
	meta: { title: 'entity.unit.name' },
	path: ':unitId',
	name: 'unit',
	props: castRouteIds,
}

const buildingCraftsmanTasks = {
	...craftsmanTasks,
	...{
		name: 'buildingCraftsmanTasks',
		component: () =>
			import(
				'components/modules/Buildings/Building/CraftsmanTasks/BuildingCraftsmanTask.vue'
			),
		children: [craftsmanTask],
		path: 'craftsman_tasks',
		meta: {
			...craftsmanTasks.meta,
			ordering: 4,
		},
	},
}

const hasHandoverProtocols: HideObject = {
	method: hasProperty,
	parameter: {
		store: 'user',
		getter: 'modules',
		property: 'handoverprotocols',
		value: true,
	},
}
const sendInvitation: RouteRecordRaw = {
	component: () => import('templates/Datatable/Selected/SendInvitation.vue'),
	meta: {
		title: 'Zugangsdaten versenden',
		auth: true,
		limited: routesLimited.can_update,
	},
	name: 'sendinvitation',
	path: 'sendinvitation',
	props: true,
}
/*const handOverProtocolCreate: RouteRecordRaw = {
	path: 'create',
	component: () =>
		import(
			'@/components/modules/Buildings/Building/HandOverProtocols/CreateEdit.vue'
		),
	meta: {
		title: 'Übergabeprotokoll anlegen',
	},
	name: 'handOverProtocolCreate',
	props: castRouteIds,
}
const handOverProtocolEdit: RouteRecordRaw = {
	path: 'edit/:handoverId',
	component: () =>
		import(
			'@/components/modules/Buildings/Building/HandOverProtocols/CreateEdit.vue'
		),
	meta: {
		title: 'Übergabeprotokoll bearbeiten',
	},
	name: 'handOverProtocolEdit',
	props: castRouteIds,
}*/
const sendInvitationPersons: RouteRecordRaw = {
	...sendInvitation,
	name: 'sendinvitationPersons',
}
const handOverProtocols: RouteRecordRaw = {
	children: [
		/*handOverProtocolCreate,
		handOverProtocolEdit,*/
		sendInvitationPersons,
	],
	component: () =>
		import(
			'@/components/modules/Buildings/Building/HandOverProtocols/List.vue'
		),
	path: 'handoverprotocols',
	meta: {
		auth: true,
		nav: true,
		icon: 'fal fa-clipboard-list',
		limited: [hasHandoverProtocols, routesLimited.isAdminOrTeam],
		title: 'Übergabeprotokolle',
		subs: [routesLimited.subModules.handover_protocols],
		ordering: 7,
	},
	name: 'handOverProtocols',
	props: castRouteIds,
}

const buildingPersonsGrouped: RouteRecordRaw = {
	component: () =>
		import('@/components/modules/Buildings/Building/Persons/Grouped.vue'),
	meta: {
		title: 'routeTitle.persons',
		subs: [routesLimited.subModules.persons],
	},
	name: 'buildingPersonsGrouped',
	path: 'grouped',
	props: castRouteIds,
}

const buildingPersonsList: RouteRecordRaw = {
	component: () =>
		import('@/components/modules/Buildings/Building/Persons/List.vue'),
	meta: {
		title: 'routeTitle.persons',
		subs: [routesLimited.subModules.persons],
	},
	name: 'buildingPersonsList',
	path: '',
	props: castRouteIds,
}
const buildingRoles: RouteRecordRaw = {
	component: () =>
		import('@/components/modules/Buildings/Building/Persons/Roles.vue'),
	meta: {
		title: 'routeTitle.roles',
	},
	name: 'buildingRoles',
	path: 'roles',
	props: castRouteIds,
}

const building: RouteRecordRaw = {
	children: [
		{
			children: cloneDeep(notes).children?.map((note) => {
				if (note && note?.name && typeof note.name === 'string') {
					note.name =
						'buildings' + note.name.charAt(0).toUpperCase() + note.name.slice(1)
				}
				if (note && note?.path) {
					note.path = '/buildings/:id/notes/' + note.path
				}
				return note
			}),
			path: '',
			name: 'building',
			components: {
				default: () =>
					import('components/modules/Buildings/Building/Info/BuildingInfo.vue'),
				header: () =>
					import('components/modules/Buildings/Building/BuildingHeader.vue'),
			},
			meta: {
				icon: 'fal fa-info-square',
				nav: true,
				title: 'Informationen',
				ordering: 0,
			},
		},
		{
			children: [buildingDocumentsList, buildingDocumentsGroup],
			components: {
				default: () =>
					import(
						'@/components/modules/Buildings/Building/Documents/Documents.vue'
					),
			},
			meta: {
				icon: 'fa-solid fa-files',
				nav: true,
				title: 'routeTitle.documents',
				subs: [routesLimited.subModules.documents],
				ordering: 1,
			},
			name: 'buildingDocuments',
			path: 'documents',
		},
		{
			path: 'units',
			name: 'buildingUnits',
			component: () =>
				import('@/components/modules/Buildings/Building/Units/Units.vue'),
			props: castRouteIds,
			meta: {
				icon: 'fal fa-buildings',
				nav: true,
				title: 'routeTitle.units',
				subs: [routesLimited.subModules.units],
				ordering: 2,
			},
			redirect: { name: 'buildingUnitsList' },
			children: [
				unit,
				{
					path: '',
					name: 'buildingUnitsList',
					component: () =>
						import(
							'@/components/modules/Buildings/Building/Units/UnitsList.vue'
						),
					props: true,
				},
			],
		},
		buildingCraftsmanTasks, //FIXME //TODO
		{
			children: [buildingPersonsList, buildingPersonsGrouped, buildingRoles],
			components: {
				default: () =>
					import('@/components/modules/Buildings/Building/Persons/Persons.vue'),
			},
			meta: {
				icon: 'fas fa-users',
				nav: true,
				title: 'routeTitle.persons',
				subs: [routesLimited.subModules.persons],
				ordering: 3,
			},
			name: 'buildingPersons',
			path: 'persons',
		},
		{
			name: 'buildingTasks',
			path: 'tasks',
			component: () =>
				import('components/modules/Buildings/Building/BuildingTasks.vue'),
			props: castRouteIds,
			meta: {
				icon: 'fa-fw fa-solid fa-clipboard-list-check',
				nav: true,
				title: 'routeTitle.tasks',
				hidden: hideTasks,
				subs: [routesLimited.subModules.tasks],
				ordering: 4,
			},
		},
		{
			children: [buildingMetersLevelInput, buildingMetersDetails],
			component: () =>
				import('@/components/modules/Buildings/Building/Meters/Meters.vue'),
			meta: {
				icon: 'fas fa-tachometer-alt',
				hidden: hideMeterReadingLimit,
				subs: [routesLimited.subModules.meters],
				nav: true,
				title: 'routeTitle.meters',
				ordering: 5,
			},
			name: 'buildingMeters',
			path: 'meters',
			props: castRouteIds,
		},
		{
			name: 'buildingCis',
			path: 'cis',
			component: () =>
				import('components/modules/Buildings/Building/BuildingCis.vue'),
			meta: {
				icon: 'fa-fw fa-solid fa-comment-lines',
				nav: true,
				title: 'entity.cis.shortcut',
				hidden: hideCis,
				subs: [routesLimited.subModules.cis],
				ordering: 6,
			},
		},
		/*handOverProtocols,*/
		buildingImageUpload,
		{
			...imagePreview,
			name: 'buildingImagePreview',
		},
		accounting,
	],

	component: () =>
		import('@/components/modules/Buildings/Building/Building.vue'),
	meta: {
		name: 'building',
		title: 'entity.building.name',
	},
	path: ':id',
	alias: aliases.building,
	props: castRouteIds,
}

const buildings = {
	children: [building],
	meta: {
		auth: true,
		icon: 'fa-fw fas fa-buildings',
		nav: true,
		ordering: 3,
		title: 'routeTitle.buildings',
		subs: [routesLimited.subModules.buildings],
		offline: true,
	},
	name: 'buildings',
	path: '/buildings',
	component: () => import('@/components/modules/Buildings/Buildings.vue'),
}

export const buildingsRoutes = [
	buildings,
	building,
	buildingPersonsGrouped,
	buildingPersonsList,
	buildingRoles,
	handOverProtocols,
	unit,
	buildingMetersLevelInput,
	buildingMetersDetails,
	buildingDocumentsGroup,
	buildingDocumentsList,
	unitPersons,
	unitDocuments,
	unitMeters,
	unitMetersDetails,
	buildingImageUpload,
	sendInvitationPersons,
]

export { buildings }
